import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Stack, Typography } from '@mui/material';
import { type FC } from 'react';
import { type Control, useController, useWatch } from 'react-hook-form';

import { type ReadRouterDeviceResponse } from '@okvc/schema';

import { CARRIER_LABELS, carrierForOperator, SIM_SLOT_LABELS, simFormValuesFromDevice } from './routerSimForm.js';
import { useReadRouterDevice } from '../../api/index.js';
import { apiErrorMessage } from '../../utils/api-error-message.js';
import { type RouterFormValues } from '../Forms/formTypes.js';

interface ReadFromRouterProps {
  control: Control<RouterFormValues>;
}

function connectedSummary({ model, activeSim }: ReadRouterDeviceResponse): string {
  if (!activeSim?.slot) {
    return `Connected to ${model}. No active SIM was reported.`;
  }
  const carrier = carrierForOperator(activeSim.operator);
  const operator = carrier === '' ? activeSim.operator : CARRIER_LABELS[carrier];
  return `Connected to ${model} · active ${SIM_SLOT_LABELS[activeSim.slot]} (${operator}). IMEI and SIM details filled in.`;
}

const ReadFromRouter: FC<ReadFromRouterProps> = ({ control }) => {
  const primarySlot = useWatch({ control, name: 'primarySlot' });
  const ipAddress = useWatch({ control, name: `sims.${primarySlot}.ipAddress` });
  const username = useWatch({ control, name: 'username' });
  const password = useWatch({ control, name: 'password' });
  const { field: imeiField } = useController({ control, name: 'imei' });
  const { field: simsField } = useController({ control, name: 'sims' });
  const readDevice = useReadRouterDevice();
  const canRead = Boolean(ipAddress && username && password);

  const fillFromDevice = (device: ReadRouterDeviceResponse) => {
    imeiField.onChange(device.imei);
    const { activeSim } = device;
    if (!activeSim?.slot) {
      return;
    }
    const simValues = simFormValuesFromDevice(simsField.value[activeSim.slot], activeSim);
    simsField.onChange({ ...simsField.value, [activeSim.slot]: simValues });
  };

  const read = () => readDevice.mutate({ ipAddress, username, password }, { onSuccess: fillFromDevice });

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <LoadingButton variant="outlined" loading={readDevice.isLoading} disabled={!canRead} onClick={read}>
          Read from router
        </LoadingButton>
        <Typography variant="body2" color="text.secondary">
          Uses the primary SIM&apos;s IP address with the username and password above.
        </Typography>
      </Stack>
      {readDevice.isError ? (
        <Alert severity="error">{apiErrorMessage(readDevice.error, 'The router could not be read')}</Alert>
      ) : null}
      {readDevice.data ? <Alert severity="success">{connectedSummary(readDevice.data)}</Alert> : null}
    </Stack>
  );
};

export default ReadFromRouter;
